import {computed, ref} from "vue";
import {createOnboardingNotification, createNotification} from "../services/notifications.fabric.js";
import {useI18n} from "vue-i18n";
import {usePage} from "@inertiajs/vue3";
import {OnboardingSteps} from "@/features/onboarding/enums/OnboardingSteps.js";
import {emitter, EmitterEvents} from "@/shared/services/Emitter.js";
import axios from 'axios';
import {routeProvider} from "@/app/route-provider.js";

const list = ref([]);
const isPolling = ref(false);
const lastFetchedAt = ref(null);
const intervalId = ref(0);
const skippedChecks = ref(0);

const notificationSound = new Audio('https://cdn.howtoespanol.com/notification.mp3');
notificationSound.volume = 0.4;

const unreadCount = computed(() => {
    return list.value.filter(n => n.unread).length
});

const playNotificationSound = () => {
    notificationSound.currentTime = 0;
    notificationSound.play();
};

export const initNotifications = async () => {
    const page = usePage();
    const backendData = page.props.notifications;

    if (backendData && Array.isArray(backendData.list)) {
        const mappedNotifications = backendData.list.map(notificationItem => {
            return createNotification({
                id: notificationItem.id,
                type: notificationItem.type,
                title: notificationItem.title,
                description: notificationItem.description,
                unread: !notificationItem.read_at,
                localized: false,
                time: new Date(notificationItem.created_at).toLocaleString(),
                link: notificationItem.link,
            });
        });

        list.value.push(...mappedNotifications);

        if (backendData.list[0]) {
            lastFetchedAt.value = backendData.list[0].created_at;
        } else {
            lastFetchedAt.value = new Date().toISOString();
        }
    }

    const userOnboarding = page.props.auth?.user?.onboarding ?? {};
    //condition that onboarding is not done
    if (!userOnboarding[`onboarding_${OnboardingSteps.finish}`]) {
        list.value.push(createOnboardingNotification());
    }

    startPolling();
}

const markManyAsRead = async (ids) => {
    if (!ids || ids.length === 0) return;

    list.value.forEach(notification => {
        if (ids.includes(notification.id)) {
            notification.unread = false;
        }
    });

    await axios.post(routeProvider.readNotifications(), { ids });
};

const fetchNewNotifications = async () => {
    if (!lastFetchedAt.value) return;

    if (document.hidden) {
        skippedChecks.value++;
        if (skippedChecks.value < 4) {
            return;
        }

        skippedChecks.value = 0;
    }

    try {
        const response = await axios.get(routeProvider.checkNotifications(), {
            params: {
                after: lastFetchedAt.value
            }
        });

        const newItems = response.data.notifications;

        if (newItems && newItems.length > 0) {
            const mappedNotifications = newItems.map(notificationItem => {
                return createNotification({
                    id: notificationItem.id,
                    type: notificationItem.type,
                    title: notificationItem.title,
                    description: notificationItem.description,
                    unread: !notificationItem.read_at,
                    localized: false,
                    time: new Date(notificationItem.created_at).toLocaleString(),
                    link: notificationItem.link,
                });
            });

            list.value = [...mappedNotifications, ...list.value];

            lastFetchedAt.value = newItems[0].created_at;

            playNotificationSound();
        }
    } catch (error) {
        console.error('Ошибка при проверке уведомлений:', error);
    }
};

const startPolling = () => {
    if (isPolling.value) return;

    isPolling.value = true;

    intervalId.value = setInterval(() => {
        fetchNewNotifications();
    }, 5000);
};

emitter.on(EmitterEvents.RemoveNotification, ({ type }) => {
    if (type) {
        list.value = list.value.filter(n => n.type !== type);
    }
})

export const useNotifications = () => {
    const { t } = useI18n();

    const localizeValue = (value) => {
        if (!value) return '';
        //@todo add localization logic for dicts
        return t(value);
    }

    const notifications = computed(() => {
        const result = [];

        for (const item of list.value) {
            const clonedItem = {...item};

            if (item.localized) {
                clonedItem.title = localizeValue(item.title);
                clonedItem.description = localizeValue(item.description);
            }

            result.push(clonedItem);
        }

        return result;
    })

    return {
        notifications, unreadCount, markManyAsRead
    }
}
